import { BUTTON_ROUNDNESS } from 'constants/button';
import { defaultWhitelabelSettings } from 'constants/defaultWhitelabel';
import { useRouter } from 'next/router';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import styled from 'styled-components';
import { lereroFetch } from 'utils/fetch';
import {
  FONT_GROUPS,
  FONT_GROUPS_1,
  FONT_GROUPS_2,
  FONT_GROUPS_3,
  FONT_GROUPS_4,
  FONT_GROUPS_5,
} from 'variables/font_groups';

const WhitelabelContext = createContext(undefined);

export const useWhitelabelContext = () => {
  return useContext(WhitelabelContext);
};

export const WhitelabelProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [whitelabelSettings, setWhitelabelSettings] = useState(defaultWhitelabelSettings);
  const [isWhitelabel, setIsWhitelabel] = useState(false);
  const router = useRouter();

  const generateRadiusInPx = useCallback((radius) => {
    return BUTTON_ROUNDNESS[radius] ?? BUTTON_ROUNDNESS.S;
  }, []);

  const generateFontGroups = useCallback((selectedHeading) => {
    return FONT_GROUPS[selectedHeading - 1] ?? FONT_GROUPS[0];
  }, []);

  const lmsDomain = (domain) => {
    return domain.replace(
      process.env.NEXT_PUBLIC_MARKETPLACE_WHITELABEL_SUFFIX,
      process.env.NEXT_PUBLIC_LMS_WHITELABEL_SUFFIX
    );
  };

  const baseDomains = [process.env.NEXT_PUBLIC_BASE_DOMAIN, 'localhost'];

  const handleGetWhitelabelSettings = async (domain) => {
    setLoading(true);
    const response = await lereroFetch('GET', `/settings/company?domain=${lmsDomain(domain)}`, {}, false, true);

    if (response.success) {
      setWhitelabelSettings(response.data);
    } else {
      setWhitelabelSettings(defaultWhitelabelSettings);
      if (!baseDomains.includes(domain)) {
        router.push(process.env.NEXT_PUBLIC_BASE_PATH);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (window) {
      const domain = window.location.hostname.replace(/^www\.(?=[^.]*\.)/i, '');
      if (!baseDomains.includes(domain)) {
        handleGetWhitelabelSettings(domain);
        setIsWhitelabel(true);
      } else {
        setLoading(false);
        setIsWhitelabel(false);
      }
    }
  }, []);

  return loading ? (
    <div className="loading">
      <Spinner animation="border" variant="white" />
    </div>
  ) : (
    <WhitelabelContext.Provider
      value={{
        header: whitelabelSettings.marketplaceHeader,
        footer: whitelabelSettings.marketplaceFooter,
        lookAndFeel: {
          ...whitelabelSettings.appearanceLookAndFeel,
          fontGroups: generateFontGroups(whitelabelSettings.appearanceLookAndFeel.typography.size),
          button: {
            ...whitelabelSettings.appearanceLookAndFeel.button,
            roundness: generateRadiusInPx(whitelabelSettings.appearanceLookAndFeel.button.roundness),
          },
        },
        company: whitelabelSettings.company,
        isWhitelabel,
        loginPage: {
          ...whitelabelSettings.appearanceLoginPage,
        },
        rest: {
          ...whitelabelSettings,
        },
      }}
    >
      <FontWrapper font={whitelabelSettings.appearanceLookAndFeel.typography.style}>{children}</FontWrapper>
    </WhitelabelContext.Provider>
  );
};

const FontWrapper = styled.div`
  font-family: ${({ font }) => (font ? `${font}, sans-serif !important` : `'Titillium Web', sans-serif !important;`)};
`;
